import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";

// Noindex wrapper for stealth routes — prevents search-engine indexing
const NoIndex = ({ children }) => (
  <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
      <meta name="googlebot" content="noindex, nofollow" />
    </Helmet>
    {children}
  </>
);

// Shared
import { SharedFooter } from "./components/SharedFooter";
import { CookieBanner } from "./components/CookieBanner";
import { Toaster } from "./components/ui/sonner";

// Gateway
import { GatewayLanding } from "./pages/GatewayLanding";

// Architecture
import { ArchHeader } from "./components/ArchHeader";
import { ArchHome } from "./pages/arch/ArchHome";
import { ArchServices } from "./pages/arch/ArchServices";
import { ArchProjects } from "./pages/arch/ArchProjects";
import { ArchAbout } from "./pages/arch/ArchAbout";
import { ArchContact } from "./pages/arch/ArchContact";
import { ArchServiceCorporate } from "./pages/arch/ArchServiceCorporate";
import { ArchServicePublic } from "./pages/arch/ArchServicePublic";
// v26.10 — `Retail & Private` service line discontinued (B2B focus).
// Route `/architecture/services/retail` now permanently redirects to
// `/architecture/services` so old inbound links land cleanly.
// import { ArchServiceRetail } from "./pages/arch/ArchServiceRetail";
import { Corp3dVisualisation } from "./pages/arch/Corp3dVisualisation";
import { CorpQuantitySurveying } from "./pages/arch/CorpQuantitySurveying";
import { CorpSustainableFitout } from "./pages/arch/CorpSustainableFitout";
import { CorpSpacePlanning } from "./pages/arch/CorpSpacePlanning";
import { PubPlaygroundDesign } from "./pages/arch/PubPlaygroundDesign";
import { PubOutdoorFurniture } from "./pages/arch/PubOutdoorFurniture";
import { PubSustainableMaterials } from "./pages/arch/PubSustainableMaterials";
import { PubConceptDesign } from "./pages/arch/PubConceptDesign";

// Engineering
import { Header } from "./components/Header";
import { Home } from "./pages/Home";
import { Studios } from "./pages/Studios";
import { StudioMaterials } from "./pages/StudioMaterials";
import { StudioInfrastructureRisk } from "./pages/StudioInfrastructureRisk";
import { StudioDigital } from "./pages/StudioDigital";
import { Projects } from "./pages/Projects";
import { Knowledge } from "./pages/Knowledge";
import { NewsletterVerify } from "./pages/NewsletterVerify";
import { NewsletterUnsubscribe } from "./pages/NewsletterUnsubscribe";
import { People } from "./pages/People";
import { Careers } from "./pages/Careers";
import { Contact } from "./pages/Contact";
import { ContactSuccess } from "./pages/ContactSuccess";
import { CarbonationCalculator } from "./pages/CarbonationCalculator";
import { CorrosivityCalculator } from "./pages/CorrosivityCalculator";
import { ThermalCalculator } from "./pages/ThermalCalculator";
import { CarbonationRisk } from "./pages/CarbonationRisk";

// Shared pages
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { LegalNotice } from "./pages/LegalNotice";
import { Admin } from "./pages/Admin";

// Auth & Identity Testing
import { AuthProvider } from "./contexts/AuthContext";
import { Login } from "./pages/auth/Login";
import { Register } from "./pages/auth/Register";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { VerifyEmail } from "./pages/auth/VerifyEmail";
import { IdentityTesting } from "./pages/identity/IdentityTesting";
import { IdentityTestingProduct } from "./pages/identity/IdentityTestingProduct";
import { AppsLanding } from "./pages/AppsLanding";
import { WhoWins } from "./pages/WhoWins";
import { ServiceLifeNexus } from "./pages/ServiceLifeNexus";
import { PriorisLanding } from "./pages/PriorisLanding";
import { PriorisApp } from "./pages/prioris/PriorisApp";

const AppLayout = () => {
  const location = useLocation();
  const isGateway = location.pathname === "/";
  const isArch = location.pathname.startsWith("/architecture");
  const isEng = location.pathname.startsWith("/engineering");
  const isAuth = location.pathname.startsWith("/auth");
  const isIdentityTesting = location.pathname === "/engineering/identity-testing";
  const isServiceLifeNexus = location.pathname === "/engineering/servicelife-nexus";
  const isPriorisApp = location.pathname === "/engineering/prioris-app";
  const isPriorisLanding = location.pathname === "/prioris"
    || location.pathname === "/engineering/prioris";
  const isWhoWins = location.pathname === "/engineering/apps/whowins";

  // v26.12 — `/` now renders the Engineering <Home /> directly
  // (architecture practice discontinued). The legacy `isGateway` exclusion
  // is no longer needed; the engineering header should show on the root
  // path same as every other engineering page.
  const showEngHeader = (isEng && !isIdentityTesting && !isServiceLifeNexus && !isPriorisApp && !isPriorisLanding && !isWhoWins) || (!isArch && !isAuth && !isIdentityTesting && !isServiceLifeNexus && !isPriorisApp && !isPriorisLanding && !isWhoWins);
  const showArchHeader = false; // architecture pages no longer routed

  return (
    <>
      {showArchHeader && <ArchHeader />}
      {showEngHeader && <Header />}

      <main>
        <Routes>
          {/* Gateway */}
          {/*
            v26.12 — Architecture practice discontinued. The legacy
            bifurcation landing (GatewayLanding) is replaced by the
            Engineering Home so visitors arriving at / land directly on
            the engineering site. The dual-brand split-screen page is
            kept on disk in case the architecture practice ever resumes.
          */}
          <Route path="/" element={<Home />} />
          <Route path="/prioris" element={<PriorisLanding />} />

          {/* Auth */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/verify-email" element={<VerifyEmail />} />

          {/* Architecture Branch — discontinued v26.12.
              All architecture URLs permanently redirect to /engineering
              so existing inbound links / search-engine indexes / bookmarks
              do not 404. Component files are retained on disk (see
              src/pages/arch/) in case the practice resumes. */}
          <Route path="/architecture" element={<Navigate to="/engineering" replace />} />
          <Route path="/architecture/*" element={<Navigate to="/engineering" replace />} />

          {/* Engineering Branch */}
          <Route path="/engineering" element={<Home />} />
          <Route path="/engineering/services" element={<Studios />} />
          <Route path="/engineering/services/materials" element={<StudioMaterials />} />
          <Route path="/engineering/services/infrastructure-risk" element={<StudioInfrastructureRisk />} />
          <Route path="/engineering/services/digital" element={<StudioDigital />} />
          <Route path="/engineering/projects" element={<Projects />} />
          <Route path="/engineering/knowledge" element={<Knowledge />} />
          <Route path="/engineering/knowledge/articles" element={<Knowledge />} />
          <Route path="/engineering/newsletter/verify" element={<NewsletterVerify />} />
          <Route path="/engineering/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
          <Route path="/engineering/people" element={<People />} />
          <Route path="/engineering/people/careers" element={<Careers />} />
          <Route path="/engineering/contact" element={<Contact />} />
          <Route path="/engineering/contact/success" element={<ContactSuccess />} />
          <Route path="/engineering/calculator/carbonation" element={<CarbonationCalculator />} />
          <Route path="/engineering/calculator/corrosivity" element={<CorrosivityCalculator />} />
          <Route path="/engineering/calculator/thermal" element={<ThermalCalculator />} />
          <Route path="/engineering/carbonation-risk" element={<NoIndex><CarbonationRisk /></NoIndex>} />
          <Route path="/engineering/identity-testing" element={<NoIndex><IdentityTesting /></NoIndex>} />
          <Route path="/engineering/prioris" element={<PriorisLanding />} />
          <Route path="/prioris" element={<PriorisLanding />} />
          <Route path="/engineering/servicelife-nexus" element={<NoIndex><PriorisApp /></NoIndex>} />
          <Route path="/engineering/prioris-app" element={<NoIndex><PriorisApp /></NoIndex>} />
          <Route path="/engineering/apps" element={<AppsLanding />} />
          <Route path="/engineering/apps/whowins" element={<NoIndex><WhoWins /></NoIndex>} />

          {/* Shared */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/legal" element={<LegalNotice />} />
          <Route path="/admin" element={<NoIndex><Admin /></NoIndex>} />
        </Routes>
      </main>

      {/* Footer: hidden on gateway, auth, identity testing, and servicelife nexus pages */}
      {!isGateway && !isAuth && !isIdentityTesting && !isServiceLifeNexus && !isPriorisApp && !isWhoWins && <SharedFooter />}

      <CookieBanner />
      <Toaster />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <AppLayout />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
